import { publicUserSchema, type PublicUser } from '@awwyeah/shared';

/**
 * Last-known-user cache (offline-first, north-star #7). When the app opens with no
 * network — a plane, a dead spot — `GET /api/auth/me` rejects before the server can say
 * anything, and without a fallback the whole app sits behind `RequireAuth`'s
 * "Checking session…" screen forever. The session cookie is still on the device and every
 * feature reads its data from IndexedDB, so the only missing piece is *who is signed in*:
 * that's what this remembers.
 *
 * Display-only trust: the server still authenticates every API call and sync socket with
 * the cookie; this never grants access to anything the device doesn't already hold. The
 * household posture is deliberately "robust, not secure" (Dan, 2026-08-11): only an
 * explicit logout() clears this — an expired session costs one online re-login, never the
 * ability to open the app offline. There is no sign-out UI today, so in practice a device
 * that has seen a user once can always open the app.
 */
export const LAST_KNOWN_USER_STORAGE_KEY = 'awwyeah:auth:last-known-user';

/** Minimal `Storage` shape so tests can pass a fake instead of real localStorage. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function readLastKnownUser(storage: StorageLike): PublicUser | null {
  let raw: string | null = null;
  try {
    raw = storage.getItem(LAST_KNOWN_USER_STORAGE_KEY);
  } catch {
    return null; // storage disabled (private mode) — behave as if nothing were cached
  }
  if (raw === null) return null;
  try {
    return publicUserSchema.parse(JSON.parse(raw));
  } catch {
    return null; // corrupt or stale-shape entry — ignore it rather than crash the shell
  }
}

export function writeLastKnownUser(storage: StorageLike, user: PublicUser): void {
  try {
    storage.setItem(LAST_KNOWN_USER_STORAGE_KEY, JSON.stringify(user));
  } catch {
    // Quota/private-mode failures just mean no offline fallback next launch.
  }
}

export function clearLastKnownUser(storage: StorageLike): void {
  try {
    storage.removeItem(LAST_KNOWN_USER_STORAGE_KEY);
  } catch {
    // Nothing useful to do — worst case a signed-out device keeps a stale display name.
  }
}
